import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SetDynamic as BLSetDynamic } from './../../../utilities/BL/Admin/Dashboard/Dynamics';
import { 
    ResetCurrentDynamic as BLResetCurrentDynamic, 
    GetAllRiddleResults as BLGetAllRiddleResults, 
    SendRiddleResultsToScreen as BLSendRiddleResultsToScreen, 
    SendRiddleQuestionToUsers as BLSendRiddleQuestionToUsers, 
    ShowItemOnScreen as BLShowItemOnScreen, 
    CloseRiddleVoting, 
    SaveRiddleGameSession, 
    GetActiveStadiumZones 
} from './../../../utilities/BL/Zones';

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { useAuth } from './../../Providers/AuthProvider';

const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#795548'];

/**
 * Resuelve una URL de imagen anteponiendo el host del backend si es relativa.
 */
const resolveAssetUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const clean = url.trim();
    if (!clean) return '';
    if (clean.startsWith('http://') || clean.startsWith('https://') || clean.startsWith('data:') || clean.startsWith('blob:')) {
        return clean;
    }
    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = apiBase.replace(/\/api\/?$/i, '');
    const normalizedPath = clean.startsWith('/') ? clean : `/${clean}`;
    return `${backendOrigin}${normalizedPath}`;
};

const GeneralResultsChart = ({ data }) => {
    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="points" fill="#8884d8">
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

const BarResultsByQuestion = ({ questionId, pointsByZone, results }) => {
    const data = results
        .map(zone => {
            const key = zone.key || (zone.text ? zone.text.toLowerCase().trim() : '');
            const points = pointsByZone ? pointsByZone[key] : 0;
            return points ? { name: zone.name || zone.text, points } : null;
        })
        .filter(Boolean);

    return (
        <div className="card shadow-sm border-0 mb-4 bg-dark text-white p-3">
            <h6 className="fw-bold text-center text-warning mb-3">Pregunta #{questionId}</h6>

            {data.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data}>
                        <XAxis dataKey="name" stroke="#adb5bd" />
                        <YAxis stroke="#adb5bd" />
                        <Tooltip contentStyle={{ backgroundColor: '#212529', borderColor: '#495057', color: '#fff' }} />
                        <Bar dataKey="points">
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <p className="text-muted text-center my-3 small">Sin puntos registrados para esta pregunta.</p>
            )}

            <div className="row row-cols-2 row-cols-md-4 g-2 mt-2">
                {data.map((zone, index) => (
                    <div key={index} className="col">
                        <div className="badge bg-secondary text-wrap w-100 py-2">
                            {zone.name}: <strong className="text-warning">{zone.points} pts</strong>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const Riddle = ({ getOutOfTheDynamic, roomId, eventId }) => {
    const { showMessage } = useFloatMessage();
    const { authenticatedFetch } = useAuth();
    const [searchParams] = useSearchParams();

    const resolvedRoomId = roomId || searchParams.get('roomId') || '';
    const resolvedEventId = eventId || searchParams.get('eventId') || '';
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

    // Estados de catálogo de adivinanzas
    const [riddlesList, setRiddlesList] = useState([]);
    const [loadingRiddles, setLoadingRiddles] = useState(false);
    const [selectedRiddleId, setSelectedRiddleId] = useState('');
    const [loadedRiddle, setLoadedRiddle] = useState(null);
    const [loadingRiddleData, setLoadingRiddleData] = useState(false);

    // Estados de juego y preguntas
    const [quizz, setQuizz] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [gameStarted, setGameStarted] = useState(false);
    const [isVotingOpen, setIsVotingOpen] = useState(false);
    const [isItemVisible, setIsItemVisible] = useState(false);

    // Modo de tiempo
    const [durationSeconds, setDurationSeconds] = useState(0); // 0 = Manual
    const [timeLeft, setTimeLeft] = useState(0);
    const timerRef = useRef(null);

    // Estados de Resultados
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState([]);
    const [questionsResults, setQuestionsResults] = useState([]);
    const [detailedResults, setDetailedResults] = useState([]);
    const [winnerInfo, setWinnerInfo] = useState(null);
    const [isSavingSession, setIsSavingSession] = useState(false);

    const [errorMessage, setErrorMessage] = useState(null);
    const [stadiumZones, setStadiumZones] = useState([]);

    // Mapeador dinámico de nombres de zona
    const resolveZoneLabel = useCallback((key) => {
        const normalizedKey = (key ?? '').toString().toLowerCase().trim();
        const matchedZone = stadiumZones.find(z => z.internalName && z.internalName.toLowerCase() === normalizedKey);
        if (matchedZone) {
            return matchedZone.displayName || matchedZone.name;
        }

        const ZONE_MAPPINGS = {
            'zona1': 'Zona 1',
            'zona2': 'Platea Zona 1',
            'zona3': 'Palcos Zona 1',
            'zona4': 'Cabecera Norte',
            'zona5': 'Cabecera Sur',
            'zona6': 'Zona 2',
            'zona7': 'Platea Zona 2',
            'zona8': 'Palcos Zona 2',
            'azul': 'Azul',
            'oro': 'Oro'
        };
        return ZONE_MAPPINGS[normalizedKey] || key;
    }, [stadiumZones]);

    // Carga de catálogo de adivinanzas desde la API V2
    const fetchRiddlesCatalog = useCallback(async () => {
        try {
            setLoadingRiddles(true);
            if (resolvedEventId) {
                const res = await authenticatedFetch(`${API_URL}v2/events/${resolvedEventId}/riddles`);
                if (res.ok) {
                    const data = await res.json();
                    setRiddlesList(Array.isArray(data) ? data : []);
                } else {
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: 'No fue posible cargar las adivinanzas del evento.'
                    });
                }
            } else {
                // Si no viene eventId, consultar eventos activos para listar todas las adivinanzas
                const resEvents = await authenticatedFetch(`${API_URL}v2/events`);
                if (resEvents.ok) {
                    const events = await resEvents.json();
                    const activeEvents = (Array.isArray(events) ? events : []).filter(e => !e.isDeleted);
                    
                    if (activeEvents.length > 0) {
                        const allRiddles = [];
                        for (const ev of activeEvents) {
                            try {
                                const resR = await authenticatedFetch(`${API_URL}v2/events/${ev.id}/riddles`);
                                if (resR.ok) {
                                    const rData = await resR.json();
                                    if (Array.isArray(rData)) {
                                        rData.forEach(r => {
                                            allRiddles.push({
                                                ...r,
                                                eventName: ev.name
                                            });
                                        });
                                    }
                                }
                            } catch (e) {
                                console.error('Error fetching riddles for event', ev.id, e);
                            }
                        }
                        setRiddlesList(allRiddles);
                    } else {
                        setRiddlesList([]);
                    }
                }
            }
        } catch (error) {
            console.error('Error in fetchRiddlesCatalog:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al consultar catálogo de adivinanzas.'
            });
        } finally {
            setLoadingRiddles(false);
        }
    }, [resolvedEventId, API_URL, authenticatedFetch, showMessage]);

    // Carga de zonas activas del estadio
    const fetchZones = useCallback(async () => {
        try {
            const zonesRes = await GetActiveStadiumZones(resolvedRoomId);
            if (zonesRes.success && Array.isArray(zonesRes.data)) {
                setStadiumZones(zonesRes.data);
            }
        } catch (error) {
            console.error('Error fetching stadium zones:', error);
        }
    }, [resolvedRoomId]);

    // Cargar detalles de una adivinanza seleccionada
    const handleLoadRiddle = async (riddleIdToLoad = selectedRiddleId) => {
        if (!riddleIdToLoad) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Por favor selecciona una adivinanza del listado.'
            });
            return;
        }

        try {
            setLoadingRiddleData(true);
            const res = await authenticatedFetch(`${API_URL}v2/riddles/${riddleIdToLoad}`);
            if (!res.ok) {
                const err = await res.json().catch(() => null);
                throw new Error(err?.message || 'Error al obtener la información de la adivinanza.');
            }

            const riddleData = await res.json();
            if (!riddleData.questions || riddleData.questions.length === 0) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'La adivinanza seleccionada no contiene preguntas configuradas.'
                });
                return;
            }

            // Formatear preguntas para la consola de control y el juego
            const formattedQuizz = riddleData.questions.map((q, idx) => ({
                id: idx + 1,
                questionId: q.id,
                instruction: q.instruction || '¿Quién se esconde en la sombra?',
                image: resolveAssetUrl(q.hiddenImageUrl),
                hiddenImageUrl: resolveAssetUrl(q.hiddenImageUrl),
                revealedImageUrl: resolveAssetUrl(q.revealedImageUrl),
                revealedText: q.revealedText || q.correctAnswer || '',
                options: (q.options || []).map((optText, optIdx) => ({
                    id: optIdx + 1,
                    text: optText,
                    isCorrect: optText.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase()
                })),
                response: {
                    text: q.revealedText || q.correctAnswer || '',
                    image: resolveAssetUrl(q.revealedImageUrl)
                },
                correctAnswer: q.correctAnswer || ''
            }));

            setQuizz(formattedQuizz);
            setCurrentIndex(0);
            setLoadedRiddle(riddleData);
            setGameStarted(false);
            setIsVotingOpen(false);
            setIsItemVisible(false);
            setShowResults(false);
            setErrorMessage(null);

            // Informar al backend de la activación de la dinámica
            await BLSetDynamic(10, riddleIdToLoad, resolvedRoomId);

            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: `"${riddleData.name}" cargada correctamente (${formattedQuizz.length} preguntas).`
            });
        } catch (error) {
            console.error('Error loading riddle:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'No fue posible cargar el cuestionario de adivinanzas.'
            });
        } finally {
            setLoadingRiddleData(false);
        }
    };

    // Temporizador visual local para preguntas con límite de tiempo
    useEffect(() => {
        if (isVotingOpen && durationSeconds > 0) {
            setTimeLeft(durationSeconds);
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            setTimeLeft(0);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isVotingOpen, durationSeconds]);

    // Retorno al dashboard
    const ReturnToDashboard = async () => {
        try {
            await BLResetCurrentDynamic(resolvedRoomId);
            if (getOutOfTheDynamic) {
                getOutOfTheDynamic();
            }
        } catch (error) {
            console.error('Error returning to dashboard:', error);
        }
    };

    // Lanzar pregunta a los usuarios y a la pantalla
    const handleLaunchQuestion = async (targetIndex = currentIndex) => {
        if (!quizz || quizz.length === 0) return;

        const targetQuestion = quizz[targetIndex];
        if (!targetQuestion) return;

        try {
            const questionPayload = {
                id: targetQuestion.id,
                instruction: targetQuestion.instruction,
                image: targetQuestion.image,
                options: targetQuestion.options,
                response: targetQuestion.response,
                durationSeconds: durationSeconds > 0 ? durationSeconds : undefined,
                roomId: resolvedRoomId || undefined
            };

            const response = await BLSendRiddleQuestionToUsers(questionPayload, resolvedRoomId);

            if (response.success) {
                setGameStarted(true);
                setIsVotingOpen(true);
                setIsItemVisible(false);
                setCurrentIndex(targetIndex);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Pregunta ${targetQuestion.id} lanzada a los aficionados y a la pantalla.`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible lanzar la pregunta.'
                });
            }
        } catch (error) {
            console.error('Error launching question:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de comunicación al lanzar la pregunta.'
            });
        }
    };

    // Cerrar votaciones de la pregunta actual
    const handleCloseVoting = async () => {
        if (!quizz[currentIndex]) return;
        const qId = quizz[currentIndex].id;

        try {
            const response = await CloseRiddleVoting(qId, resolvedRoomId);
            if (response.success) {
                setIsVotingOpen(false);
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    text: `Votaciones cerradas para la pregunta ${qId}.`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No se pudo cerrar la votación.'
                });
            }
        } catch (error) {
            console.error('Error closing voting:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al cerrar las votaciones.'
            });
        }
    };

    // Mostrar elemento oculto (revelado) en pantalla
    const handleShowItemOnScreen = async () => {
        if (!quizz[currentIndex]) return;
        const currentQuestion = quizz[currentIndex];

        try {
            const response = await BLShowItemOnScreen(currentQuestion, resolvedRoomId);
            if (response.success) {
                setIsItemVisible(true);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Mostrando elemento revelado en pantalla.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible mostrar el elemento en pantalla.'
                });
            }
        } catch (error) {
            console.error('Error showing item on screen:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al mostrar el elemento en pantalla.'
            });
        }
    };

    // Siguiente pregunta
    const handleNext = async () => {
        if (currentIndex < quizz.length - 1) {
            const nextIdx = currentIndex + 1;
            setCurrentIndex(nextIdx);
            setIsItemVisible(false);

            if (gameStarted) {
                await handleLaunchQuestion(nextIdx);
            }
        }
    };

    // Pregunta anterior
    const handlePrev = async () => {
        if (currentIndex > 0) {
            const prevIdx = currentIndex - 1;
            setCurrentIndex(prevIdx);
            setIsItemVisible(false);

            if (gameStarted) {
                await handleLaunchQuestion(prevIdx);
            }
        }
    };

    // Mostrar Resultados Finales y persistir en BD
    const ShowResultsView = async () => {
        try {
            setErrorMessage(null);

            // 1. Obtener los resultados acumulados
            const response = await BLGetAllRiddleResults(resolvedRoomId);

            if (!response.success) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible obtener los resultados de las adivinanzas.'
                });
                setErrorMessage('No fue posible obtener los resultados de las adivinanzas.');
                return;
            }

            const resultsByQuestion = response.data || [];
            if (resultsByQuestion.length === 0) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'Aún no hay respuestas registradas en las adivinanzas.'
                });
                setErrorMessage('Aún no hay resultados de las adivinanzas.');
                return;
            }

            setDetailedResults(resultsByQuestion);

            // 2. Extraer zonas participantes y calcular totales
            const uniqueZones = new Set();
            resultsByQuestion.forEach(result => {
                const points = result.calculatedPointsByZone || {};
                Object.keys(points).forEach(zone => uniqueZones.add(zone));
                const users = result.connectedUsersByZoneAtStart || {};
                Object.keys(users).forEach(zone => uniqueZones.add(zone));
            });

            if (uniqueZones.size === 0) {
                stadiumZones.forEach(z => uniqueZones.add(z.internalName));
            }
            if (uniqueZones.size === 0) {
                uniqueZones.add('zona1');
                uniqueZones.add('zona2');
            }

            const zonesTemplate = Array.from(uniqueZones).map(zoneKey => {
                const text = resolveZoneLabel(zoneKey);
                return { name: text, text: zoneKey, points: 0, show: true };
            });

            const totalPointsByZone = {};
            zonesTemplate.forEach(zone => {
                totalPointsByZone[zone.text.toLowerCase()] = 0;
            });

            const questionsBreakdown = resultsByQuestion.map(result => {
                const questionId = result.id;
                const pointsPerZone = result.calculatedPointsByZone || {};
                const normalizedPerQuestion = {};

                Object.entries(pointsPerZone).forEach(([zoneName, pts]) => {
                    const key = zoneName.toLowerCase().trim();
                    if (totalPointsByZone.hasOwnProperty(key)) {
                        totalPointsByZone[key] += pts;
                    } else {
                        totalPointsByZone[key] = pts;
                    }
                    normalizedPerQuestion[key] = pts;
                });

                return {
                    questionId,
                    pointsByZone: normalizedPerQuestion
                };
            });

            const finalResultsPerZone = zonesTemplate.map(zone => {
                const key = zone.text.toLowerCase();
                const points = Math.round((totalPointsByZone[key] || 0) * 100) / 100;
                return {
                    ...zone,
                    key,
                    points,
                    show: points > 0
                };
            });

            // Determinar ganador / empate
            let winnerZone = null;
            let isTotalTie = false;
            let tiedZones = [];
            const sortedByPoints = [...finalResultsPerZone].sort((a, b) => b.points - a.points);
            
            if (sortedByPoints.length > 0 && sortedByPoints[0].points > 0) {
                const maxPoints = sortedByPoints[0].points;
                const topZones = sortedByPoints.filter(z => Math.abs(z.points - maxPoints) < 0.001);
                if (topZones.length === 1) {
                    winnerZone = topZones[0].text;
                    isTotalTie = false;
                } else {
                    isTotalTie = true;
                    tiedZones = topZones.map(z => z.text);
                    winnerZone = tiedZones.join(', ');
                }
            }

            setWinnerInfo({
                winnerZone,
                isTotalTie,
                tiedZones,
                winnerLabel: winnerZone ? resolveZoneLabel(winnerZone) : 'Sin definir'
            });

            setResults(finalResultsPerZone);
            setQuestionsResults(questionsBreakdown);
            setShowResults(true);

            // 3. Emitir inmediatamente a la pantalla (Sin latencia)
            const responseShowResults = await BLSendRiddleResultsToScreen(resolvedRoomId);
            if (responseShowResults.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Resultados proyectados en pantalla exitosamente!'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: responseShowResults.message || 'Error al proyectar resultados en pantalla.'
                });
            }

            // 4. Persistir de forma asíncrona no bloqueante en BD (POST /api/v2/riddle-results/save-session)
            const zonesMetaObj = {};
            finalResultsPerZone.forEach(z => {
                zonesMetaObj[z.text] = {
                    label: resolveZoneLabel(z.text),
                    color: null
                };
            });

            const sessionPayload = {
                roomId: resolvedRoomId || undefined,
                riddleId: loadedRiddle?.id || selectedRiddleId,
                winnerZoneInternal: winnerZone,
                winnerZoneLabel: winnerZone ? resolveZoneLabel(winnerZone) : '',
                isTotalTie: isTotalTie,
                tiedZones: tiedZones,
                zonesMeta: zonesMetaObj,
                questionResults: questionsBreakdown.map((qr, idx) => ({
                    questionIndex: qr.questionId || (idx + 1),
                    pointsByZone: qr.pointsByZone
                }))
            };

            setIsSavingSession(true);
            SaveRiddleGameSession(sessionPayload)
                .then(saveRes => {
                    if (saveRes?.success) {
                        console.log('[Riddle] Sesión guardada en Base de Datos exitosamente.');
                    } else {
                        console.warn('[Riddle] Advertencia al persistir sesión en BD:', saveRes?.message);
                    }
                })
                .catch(err => {
                    console.error('[Riddle] Error al persistir la sesión de juego:', err);
                })
                .finally(() => {
                    setIsSavingSession(false);
                });

        } catch (error) {
            console.error('Error in ShowResultsView:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Ocurrió un error al procesar los resultados.'
            });
            setErrorMessage('Ocurrió un error al procesar los resultados.');
        }
    };

    // Render de listas clave:valor para estadísticas
    const KeyValueList = ({ data }) => {
        if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
            return <p className="text-muted small mb-0">0 datos registrados</p>;
        }

        const entries = Object.entries(data).sort((a, b) =>
            a[0].localeCompare(b[0], 'es', { sensitivity: 'base' })
        );

        return (
            <ul className="list-unstyled mb-0 small">
                {entries.map(([zone, value]) => (
                    <li key={zone} className="d-flex justify-content-between py-1 border-bottom border-secondary border-opacity-25">
                        <span className="text-muted">{resolveZoneLabel(zone)}:</span>
                        <strong className="text-warning">{value}</strong>
                    </li>
                ))}
            </ul>
        );
    };

    // Inicialización del componente
    useEffect(() => {
        document.getElementById('root')?.classList.add('BodyComplete');
        fetchZones();
        fetchRiddlesCatalog();
    }, [fetchZones, fetchRiddlesCatalog]);

    const currentQuestion = quizz[currentIndex] || null;

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <div className="container-fluid py-3" style={{ maxWidth: '1100px' }}>
                
                {/* ENCABEZADO DE CONSOLA */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-2 border-bottom border-secondary border-opacity-50">
                    <div className="d-flex align-items-center">
                        <div className="bg-warning text-dark p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                            <i className="bi bi-question-diamond-fill fs-4"></i>
                        </div>
                        <div>
                            <h4 className="fw-bold mb-0 text-white">Consola de Control: Adivina Adivinador</h4>
                            <span className="text-muted small">
                                {resolvedRoomId ? `Sala: ${resolvedRoomId}` : 'Modo Directo'}
                                {loadedRiddle && ` • Adivinanza: "${loadedRiddle.name}"`}
                            </span>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                        {gameStarted ? (
                            <span className="badge bg-success px-3 py-2 fs-6">
                                <i className="bi bi-broadcast me-1"></i> Dinámica en Curso
                            </span>
                        ) : (
                            <span className="badge bg-secondary px-3 py-2 fs-6">
                                <i className="bi bi-pause-circle me-1"></i> En Espera
                            </span>
                        )}

                        {isVotingOpen && (
                            <span className="badge bg-info text-dark px-3 py-2 fs-6 animate__animated animate__pulse animate__infinite">
                                <i className="bi bi-check2-circle me-1"></i> Votación Abierta
                                {durationSeconds > 0 && ` (${timeLeft}s)`}
                            </span>
                        )}

                        {isItemVisible && (
                            <span className="badge bg-warning text-dark px-3 py-2 fs-6">
                                <i className="bi bi-eye-fill me-1"></i> Silueta Revelada
                            </span>
                        )}
                    </div>
                </div>

                {/* VISTA DE RESULTADOS */}
                {showResults ? (
                    <div className="animate__animated animate__fadeIn">
                        {/* TARJETA DE GANADOR / EMPATE */}
                        <div className="card border-0 shadow-lg mb-4 text-center bg-dark text-white p-4" style={{ borderRadius: '15px', border: '2px solid #ffc107' }}>
                            <i className="bi bi-trophy-fill text-warning display-3 mb-2 animate__animated animate__bounce"></i>
                            <h3 className="fw-bold text-warning mb-1">
                                {winnerInfo?.isTotalTie ? '¡Empate en el Primer Lugar!' : '¡Zona Ganadora!'}
                            </h3>
                            <h2 className="display-6 fw-bold text-white mb-2">
                                {winnerInfo?.winnerLabel || 'Sin puntuaciones'}
                            </h2>
                            {isSavingSession && (
                                <span className="text-info small">
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Guardando resultados en BD...
                                </span>
                            )}
                        </div>

                        {/* GRÁFICA GENERAL */}
                        <div className="card border-0 shadow-sm mb-4 bg-dark text-white p-3">
                            <h5 className="fw-bold text-warning mb-3 text-center">
                                <i className="bi bi-bar-chart-fill me-2"></i> Puntuación Total Acumulada por Zona
                            </h5>
                            <GeneralResultsChart data={results.filter(r => r.show || r.points > 0)} />
                        </div>

                        {/* DESGLOSE POR PREGUNTA */}
                        <h5 className="fw-bold text-warning my-4">
                            <i className="bi bi-list-stars me-2"></i> Desglose Detallado por Pregunta
                        </h5>

                        <div className="row row-cols-1 row-cols-md-2 g-3">
                            {questionsResults.map((qr, idx) => (
                                <div key={idx} className="col">
                                    <BarResultsByQuestion
                                        questionId={qr.questionId}
                                        pointsByZone={qr.pointsByZone}
                                        results={results}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* ESTADÍSTICAS DETALLADAS */}
                        <div className="card border-0 shadow-sm mt-4 bg-dark text-white p-4">
                            <h5 className="fw-bold text-warning mb-3">
                                <i className="bi bi-pie-chart-fill me-2"></i> Estadísticas por Zona
                            </h5>

                            <div className="row row-cols-1 row-cols-md-3 g-3">
                                {detailedResults.map((q) => (
                                    <div key={q.id} className="col">
                                        <div className="card bg-secondary bg-opacity-10 border border-secondary p-3 h-100">
                                            <h6 className="fw-bold text-warning border-bottom border-secondary pb-2 mb-3">
                                                Pregunta #{q.id}
                                            </h6>

                                            <p className="text-white small fw-bold mb-1">Aficionados Conectados:</p>
                                            <KeyValueList data={q.connectedUsersByZoneAtStart} />

                                            <p className="text-white small fw-bold mt-3 mb-1">Puntos Asignados:</p>
                                            <KeyValueList data={q.calculatedPointsByZone} />

                                            <p className="text-white small fw-bold mt-3 mb-1">Respuestas Correctas:</p>
                                            <KeyValueList data={q.correctAnswersByZone} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* BOTÓN REGRESAR A CONSOLA */}
                        <div className="text-center my-4">
                            <button 
                                type="button" 
                                className="btn btn-outline-warning btn-lg px-5 shadow"
                                onClick={() => setShowResults(false)}
                            >
                                <i className="bi bi-arrow-left me-2"></i> Volver a la Consola
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* SECCIÓN 1: SELECTOR ESTRICTO Y CONFIGURACIÓN */}
                        <div className="card border-0 shadow-sm mb-4 bg-dark text-white p-4" style={{ borderRadius: '12px' }}>
                            <div className="row g-3 align-items-end">
                                
                                {/* SELECTOR ESTRICTO DE ADIVINANZA */}
                                <div className="col-12 col-md-5">
                                    <label className="form-label text-warning small fw-bold">
                                        <i className="bi bi-journal-text me-1"></i> Seleccionar Adivinanza (API V2) <span className="text-danger">*</span>
                                    </label>
                                    <select 
                                        className="form-select bg-dark text-white border-secondary"
                                        value={selectedRiddleId}
                                        onChange={(e) => setSelectedRiddleId(e.target.value)}
                                        disabled={loadingRiddles || loadingRiddleData}
                                    >
                                        <option value="">-- Selecciona una adivinanza --</option>
                                        {riddlesList.map((r) => (
                                            <option key={r.id} value={r.id}>
                                                {r.name} ({r.questionsCount || (r.questions && r.questions.length) || 0} preguntas) {r.eventName ? `• ${r.eventName}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {loadingRiddles && <span className="text-muted small">Cargando catálogo...</span>}
                                </div>

                                {/* MODO DE TIEMPO */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label text-warning small fw-bold">
                                        <i className="bi bi-stopwatch me-1"></i> Modo de Tiempo por Pregunta
                                    </label>
                                    <select
                                        className="form-select bg-dark text-white border-secondary"
                                        value={durationSeconds}
                                        onChange={(e) => setDurationSeconds(Number(e.target.value))}
                                        disabled={isVotingOpen}
                                    >
                                        <option value={0}>Manual (Sin tiempo límite)</option>
                                        <option value={10}>10 Segundos</option>
                                        <option value={15}>15 Segundos</option>
                                        <option value={20}>20 Segundos</option>
                                        <option value={30}>30 Segundos</option>
                                    </select>
                                </div>

                                {/* BOTÓN CARGAR ADIVINANZA */}
                                <div className="col-12 col-md-3">
                                    <button
                                        type="button"
                                        className="btn btn-warning w-100 fw-bold shadow-sm"
                                        onClick={() => handleLoadRiddle(selectedRiddleId)}
                                        disabled={!selectedRiddleId || selectedRiddleId === '' || loadingRiddleData}
                                    >
                                        {loadingRiddleData ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Cargando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-arrow-clockwise me-1"></i> Cargar Adivinanza
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* SECCIÓN 2: ESCENARIO DE PREGUNTA ACTUAL */}
                        {quizz.length > 0 && currentQuestion ? (
                            <div className="card border-0 shadow-lg mb-4 bg-dark text-white p-4" style={{ borderRadius: '15px' }}>
                                
                                {/* NAVEGACIÓN Y CONTADOR */}
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-secondary">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-light btn-sm"
                                        onClick={handlePrev}
                                        disabled={currentIndex === 0}
                                    >
                                        <i className="bi bi-chevron-left me-1"></i> Anterior
                                    </button>

                                    <div className="text-center">
                                        <span className="badge bg-warning text-dark px-3 py-2 fs-6">
                                            Pregunta {currentIndex + 1} de {quizz.length}
                                        </span>
                                    </div>

                                    <button 
                                        type="button" 
                                        className="btn btn-outline-light btn-sm"
                                        onClick={handleNext}
                                        disabled={currentIndex === quizz.length - 1}
                                    >
                                        Siguiente <i className="bi bi-chevron-right ms-1"></i>
                                    </button>
                                </div>

                                {/* INSTRUCCIÓN DE LA ADIVINANZA */}
                                <div className="text-center my-3">
                                    <p className="text-muted small mb-1 text-uppercase fw-bold">Instrucción / Pista:</p>
                                    <h4 className="fw-bold text-warning">{currentQuestion.instruction}</h4>
                                </div>

                                {/* CONTENEDOR VISUAL: SILUETA VS REVELADA */}
                                <div className="row align-items-center justify-content-center my-3 g-4">
                                    
                                    {/* Silueta (Oculta) */}
                                    <div className="col-12 col-md-5 text-center">
                                        <div className={`p-3 rounded-3 border ${!isItemVisible ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}>
                                            <p className="small text-muted fw-bold mb-2">
                                                <i className="bi bi-moon-stars-fill text-warning me-1"></i> Silueta Oculta (Aficionados)
                                            </p>
                                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '220px' }}>
                                                {currentQuestion.hiddenImageUrl ? (
                                                    <img 
                                                        src={currentQuestion.hiddenImageUrl} 
                                                        alt="Silueta" 
                                                        className="img-fluid rounded shadow"
                                                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <span className="text-muted small">Sin imagen</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Flecha divisoria */}
                                    <div className="col-12 col-md-1 text-center d-none d-md-block">
                                        <i className="bi bi-arrow-right-circle-fill fs-2 text-warning"></i>
                                    </div>

                                    {/* Revelada */}
                                    <div className="col-12 col-md-5 text-center">
                                        <div className={`p-3 rounded-3 border ${isItemVisible ? 'border-success bg-success bg-opacity-10' : 'border-secondary'}`}>
                                            <p className="small text-muted fw-bold mb-2">
                                                <i className="bi bi-eye-fill text-success me-1"></i> Imagen Revelada (Solución)
                                            </p>
                                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '220px' }}>
                                                {currentQuestion.revealedImageUrl ? (
                                                    <img 
                                                        src={currentQuestion.revealedImageUrl} 
                                                        alt="Revelada" 
                                                        className="img-fluid rounded shadow"
                                                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <span className="text-muted small">Sin imagen</span>
                                                )}
                                            </div>
                                            {currentQuestion.revealedText && (
                                                <p className="fw-bold text-success mt-2 mb-0">
                                                    Solución: {currentQuestion.revealedText}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* OPCIONES DE RESPUESTA */}
                                <div className="mt-3">
                                    <p className="text-center text-muted small fw-bold mb-2">Opciones de Respuesta:</p>
                                    <div className="row row-cols-1 row-cols-md-2 g-2 justify-content-center">
                                        {currentQuestion.options.map((opt, idx) => (
                                            <div key={idx} className="col">
                                                <div className={`p-2 rounded border text-center ${opt.isCorrect ? 'border-success bg-success bg-opacity-25 text-success fw-bold' : 'border-secondary bg-dark text-white'}`}>
                                                    {opt.isCorrect && <i className="bi bi-check-circle-fill me-2 text-success"></i>}
                                                    {opt.text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* SECCIÓN 3: BOTONES DE CONTROL DE PREGUNTA */}
                                <div className="card border-0 bg-secondary bg-opacity-10 p-3 mt-4 rounded-3">
                                    <p className="text-center text-warning fw-bold small mb-3">
                                        <i className="bi bi-sliders me-1"></i> Acciones del Operador
                                    </p>

                                    <div className="d-flex flex-wrap justify-content-center gap-3">
                                        
                                        {/* LANZAR PREGUNTA */}
                                        <button 
                                            type="button" 
                                            className="btn btn-warning px-4 py-2 fw-bold shadow"
                                            onClick={() => handleLaunchQuestion(currentIndex)}
                                        >
                                            <i className="bi bi-play-circle-fill me-1"></i> {gameStarted ? 'Relanzar Pregunta' : 'Lanzar Pregunta / Iniciar'}
                                        </button>

                                        {/* CERRAR VOTACIONES */}
                                        <button 
                                            type="button" 
                                            className={`btn px-4 py-2 fw-bold shadow ${isVotingOpen ? 'btn-danger' : 'btn-outline-danger'}`}
                                            onClick={handleCloseVoting}
                                            disabled={!isVotingOpen}
                                        >
                                            <i className="bi bi-slash-circle me-1"></i> Cerrar Votaciones
                                        </button>

                                        {/* MOSTRAR ELEMENTO OCULTO */}
                                        <button 
                                            type="button" 
                                            className={`btn px-4 py-2 fw-bold shadow ${!isItemVisible ? 'btn-info' : 'btn-outline-info'}`}
                                            onClick={handleShowItemOnScreen}
                                            disabled={isItemVisible}
                                        >
                                            <i className="bi bi-eye me-1"></i> {isItemVisible ? 'Elemento Revelado en Pantalla' : 'Mostrar Elemento Oculto'}
                                        </button>

                                        {/* FINALIZAR Y VER RESULTADOS */}
                                        {gameStarted && (
                                            <button 
                                                type="button" 
                                                className="btn btn-success px-4 py-2 fw-bold shadow"
                                                onClick={ShowResultsView}
                                            >
                                                <i className="bi bi-trophy me-1"></i> Finalizar y Mostrar Resultados
                                            </button>
                                        )}
                                    </div>

                                    {errorMessage && (
                                        <p className="text-danger small text-center mt-3 mb-0">
                                            <i className="bi bi-exclamation-triangle-fill me-1"></i> {errorMessage}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* ESTADO VACÍO: ADIVINANZA NO CARGADA */
                            <div className="card border-0 shadow-sm text-center bg-dark text-white p-5" style={{ borderRadius: '15px' }}>
                                <i className="bi bi-question-square-fill text-muted display-3 mb-3"></i>
                                <h4 className="fw-bold text-white mb-2">Ninguna Adivinanza Cargada</h4>
                                <p className="text-muted" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                    Selecciona un cuestionario de adivinanzas del menú desplegable superior y presiona <strong>"Cargar Adivinanza"</strong> para comenzar a operar la dinámica.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </SecondaryBase>
    );
};

export { Riddle };
export default Riddle;